//
// # Layout
//

// Workspace Imports
import {
  logger,
  Result,
  validate as v,
  change,
  Uuid,
  Context,
} from "CoreUtils";
import * as schema from "GenAlphaSchema";
import { DepotGet } from "Depot";
// Local Imports
import { Bbox, Size, Point, Xform, Orientation } from "../core";
import * as graphics from "../graphics";
import { Ident, IdentMap, IdentSet } from "../ident";
import { randomId } from "../id";

// This runs on client too, so don't use logger

export type LayoutChild = schema.layout.LayoutChild;
export type LayoutChange = schema.layout.LayoutChange;
export const LayoutChange = {
  // Create an `Add` change
  add: (data: LayoutChild): LayoutChange => {
    return {
      contentKind: "layout",
      changeKind: "add",
      data,
    };
  },
  // Create a `Remove` change
  remove: (childKind: LayoutChild["targetKind"], id: number): LayoutChange => {
    return {
      contentKind: "layout",
      changeKind: "remove",
      id,
      data: { targetKind: childKind },
    };
  },
  // Create an `Update` change
  update: (id: number, data: change.Update<LayoutChild>): LayoutChange => {
    return {
      contentKind: "layout",
      changeKind: "update",
      id,
      data,
    };
  },
  setOutline: (outline: schema.Bbox): LayoutChange => {
    return {
      contentKind: "layout",
      changeKind: "set-outline",
      data: { outline },
    };
  },
  removeOutline: (): LayoutChange => {
    return {
      contentKind: "layout",
      changeKind: "remove-outline",
    };
  },
  validator: new v.Validator(v.createValidate<LayoutChange>()),
};

// Schema re-exports & type aliases
export type Stack = schema.pdk.Stack;
export type LayoutXform = schema.layout.LayoutXform;
export type ArrayXform = schema.layout.ArrayXform;
export type MetalLayer = schema.metal.MetalLayer;
export type Conn = schema.layout.Conn;
export type PortConn = schema.layout.PortConn;

// Type replacements for layer references
/** A route point with resolved MetalLayer instead of layerId */
export interface ModelRoutePoint {
  point: schema.core.Point;
  layer: schema.metal.MetalLayer;
  // Explicit width (nm) of the segment leaving this point; see schema RoutePoint.
  width?: number | null;
}

/** Route with resolved MetalLayer objects in path */
export type Route = Omit<schema.layout.Route, "path"> & {
  path: Array<ModelRoutePoint>;
};
export type Blockage = Omit<schema.layout.Blockage, "layer"> & {
  layer: schema.metal.MetalLayer;
};
/**
 * A layout pin is the schema's: it is its shapes, each on its own layer, and
 * has no layer or point of its own to resolve.
 */
export type LayoutPin = schema.layout.LayoutPin;

// Visual size of a point pin shape, which has no area of its own, for bbox
// calculations and rendering: the model (import-time bbox) and the editor
// (PinEntity drawing/hit-testing) both use it.
export const PIN_SIZE: Size = { width: 100, height: 100 };

/** A pin shape as a drawable graphic; a point draws as a PIN_SIZE box. */
export function pinShapeGraphic(shape: schema.layout.PinShape): graphics.Shape {
  switch (shape.kind) {
    case "rect":
      return { kind: "rectangle", bbox: shape.bbox, filled: true };
    case "polygon":
      return { kind: "polygon", points: shape.points, filled: true };
    case "path":
      return { kind: "path", points: shape.points, style: shape.style };
    case "point":
      return {
        kind: "rectangle",
        bbox: Bbox.fromSizeAndCenter(PIN_SIZE, shape.at),
        filled: true,
      };
  }
}

/**
 * The box a pin shape covers, including a path's width and end extensions.
 * A point covers the PIN_SIZE box it draws as.
 */
export function pinShapeBbox(shape: schema.layout.PinShape): Bbox {
  switch (shape.kind) {
    case "rect":
      return Bbox.clone(shape.bbox);
    case "polygon":
      return Bbox.fromPoints(...shape.points);
    case "path": {
      const { width, beginExt, endExt } = shape.style;
      const d = Math.max(width / 2, beginExt, endExt);
      const b = Bbox.fromPoints(...shape.points);
      return {
        min: { x: b.min.x - d, y: b.min.y - d },
        max: { x: b.max.x + d, y: b.max.y + d },
      };
    }
    case "point":
      return Bbox.fromSizeAndCenter(PIN_SIZE, shape.at);
  }
}

/** The box a pin's shapes cover. */
export function pinBbox(pin: Pick<LayoutPin, "shapes">): Bbox {
  return Bbox.getBboxUnion(pin.shapes.map((s) => pinShapeBbox(s.shape)));
}

/** A pin shape moved by `d`. */
export function translatePinShape(
  shape: schema.layout.PinShape,
  d: Point
): schema.layout.PinShape {
  const mv = (p: Point): Point => ({ x: p.x + d.x, y: p.y + d.y });
  switch (shape.kind) {
    case "rect":
      return {
        kind: "rect",
        bbox: { min: mv(shape.bbox.min), max: mv(shape.bbox.max) },
      };
    case "polygon":
      return { kind: "polygon", points: shape.points.map(mv) };
    case "path":
      return {
        kind: "path",
        points: shape.points.map(mv),
        style: { ...shape.style },
      };
    case "point":
      return { kind: "point", at: mv(shape.at) };
  }
}

/** A pin's shapes moved by `d`. */
export function translatePinShapes(
  shapes: schema.layout.LayoutPin["shapes"],
  d: Point
): schema.layout.LayoutPin["shapes"] {
  const mv = (s: schema.layout.LayoutPinShape) => ({
    ...s,
    shape: translatePinShape(s.shape, d),
  });
  const [first, ...rest] = shapes;
  return [mv(first), ...rest.map(mv)];
}

/**
 * Where a pin is grabbed to move it: the centre of the box its shapes cover,
 * rounded down to the integer grid.
 */
export function pinAnchor(pin: Pick<LayoutPin, "shapes">): Point {
  const b = pinBbox(pin);
  return {
    x: Math.floor((b.min.x + b.max.x) / 2),
    y: Math.floor((b.min.y + b.max.y) / 2),
  };
}

// v4: Layout is the single layout type (no more LayoutCell/LayoutTree distinction)
export type LayoutCell = schema.Layout;
export const LayoutCell = {
  new: (ident: Ident): LayoutCell => {
    return {
      schemaVersion: schema.SCHEMA_VERSION,
      ident,
      contentKind: "layout",
      abs: {
        bbox: graphics.Bbox.empty(),
        pins: [],
        blockages: [],
      },
      impl: {
        elems: [],
        instances: [],
        routes: [],
        conns: [],
      },
    };
  },

  // Returns the list of layouts instantiated by this ShallowLayout.
  // FIXME! this will compile but not actually work!
  getReferences: (root: LayoutCell): IdentSet => {
    const set = new IdentSet();
    for (const instance of root.impl?.instances ?? []) {
      set.add(instance.target);
    }
    return set;
  },

  // Assign unique IDs to all the elements in a layout, preserving existing IDs.
  withIDs: (layout: LayoutCell): LayoutCell => {
    for (const pin of layout.abs.pins) {
      pin.id = pin.id ?? randomId();
    }
    for (const blockage of layout.abs.blockages) {
      blockage.id = blockage.id ?? randomId();
    }
    if (!layout.impl) return layout;
    for (const inst of layout.impl.instances) {
      inst.id = inst.id ?? randomId();
    }
    for (const route of layout.impl.routes) {
      route.id = route.id ?? randomId();
    }
    return layout;
  },
};

export interface Abstract {
  bbox: Bbox;
  outline?: Bbox;
  pins: Array<LayoutPin>;
  blockages: Array<Blockage>;
}

// # Impl
// Layout Implementation
export interface Impl {
  elems: Array<graphics.LayeredElement>; // Implementation Content
  instances: Array<Instance>; // Instances
  routes: Array<Route>; // Routes
  conns: Array<Conn>; // Connections
}

export type Instance = Omit<schema.layout.Instance, "target"> & {
  target: BoundTarget | UnboundTarget;
};

export interface BoundTarget {
  kind: "bound";
  layout: Layout;
}

export interface UnboundTarget {
  kind: "unbound";
  ident: Ident;
}

// Type aliases for concrete instance variants
export type ScalarInstance = Instance & { place: schema.layout.ScalarPlace };
export type SegmentedInstance = Instance & {
  place: schema.layout.SegmentedPlace;
};
export type ArrayInstance = Instance & { place: schema.layout.ArrayPlace };

// # Layout
export class Layout {
  contentKind = "layout" as const; // Content Kind Tag
  static layouts = new IdentMap<Layout>(); // Ident-based map of layouts in memory
  static stacks = new Map<schema.StackId, Stack>(); // Map of stacks in memory

  constructor(
    readonly ident: Ident,
    readonly abs: Abstract,
    readonly impl?: Impl,
    readonly stack?: Stack,
    readonly properties?: Array<schema.Property>,
    readonly pdkId?: string,
    readonly pdk?: schema.pdk.PDK
  ) {
    this.ident = ident;
    this.abs = abs;
    this.impl = impl;
    this.stack = stack;
    this.properties = properties;
    this.pdkId = pdkId;
    this.pdk = pdk;
    Layout.layouts.set(this);
  }

  // Drop a design's cached import so the next `getLayout` re-parses it from the
  // current source (state or depot). The cache is keyed by ident with no
  // version, so a design's content changing does NOT update it — callers MUST
  // invalidate whenever content changes, or a stale import is served
  // indefinitely. (See the state service's apply / state-replaced / save paths
  // and EditorCore's load path for the invalidation sites.)
  static invalidate(ident: schema.Ident): void {
    Layout.layouts.delete(ident);
  }

  // Drop the whole cache — e.g. when loading fresh top-level content.
  static invalidateAll(): void {
    Layout.layouts.clear();
  }

  static async getLayout(
    ctx: Context,
    ident: schema.Ident,
    depot: DepotGet,
    namespace: string
  ): Promise<Result<Layout>> {
    // Check the class level cache
    const existing = Layout.layouts.get(ident);
    if (existing) {
      return Result.Ok(existing);
    }

    // Not found - load from depot
    const contentResult = await depot.content.get(ctx, ident, namespace);
    if (contentResult.err) {
      return contentResult;
    }
    if (contentResult.val.contentKind !== "layout") {
      return Result.Err({ why: "invalid-layout-content-kind" });
    }

    // Import and return it
    // Note the constructor adds the `Layout` to the cache
    return Layout.fromSchema(ctx, contentResult.val, depot, namespace);
  }

  // Get a `Stack` from an `Id`, either from the cache or from the depot
  static async getStack(
    pdkId: string,
    stackId: schema.StackId,
    depot: DepotGet,
    namespace?: string
  ): Promise<Result<Stack>> {
    //
    // Check the class level cache
    const existing = Layout.stacks.get(stackId);
    if (existing) return Result.Ok(existing);

    // Not found - load from depot
    const stackResult = await depot.stack.get(pdkId, stackId, namespace);
    if (stackResult.err) return stackResult;

    // Add it to the cache and return it
    Layout.stacks.set(stackId, stackResult.val);
    return Result.Ok(stackResult.val);
  }

  // Convert Layout schema object into a Layout
  static async fromSchema(
    ctx: Context,
    layout: schema.Layout,
    depot: DepotGet,
    namespace: string
  ): Promise<Result<Layout>> {
    let stack: Stack | undefined;
    let pdk: schema.pdk.PDK | undefined;

    // Load the stack if specified
    if (layout.pdkId && layout.stackId) {
      const stackResult = await Layout.getStack(
        layout.pdkId,
        layout.stackId,
        depot,
        namespace
      );
      if (stackResult.err) {
        return stackResult;
      }
      stack = stackResult.val;
    }

    // Load the PDK if specified
    if (layout.pdkId) {
      const pdkResult = await depot.pdk.get(layout.pdkId, namespace);
      if (pdkResult.ok) {
        pdk = pdkResult.val;
      } else {
        logger.warn(
          `[Layout.fromSchema] Failed to load PDK ${layout.pdkId}:`,
          pdkResult.val
        );
      }
    }

    // Create importer to convert schema types to model types
    const importer = new Importer(depot, namespace, stack, layout.pdkId, pdk);
    return importer.importLayout(ctx, layout);
  }

  // Export to v6 Layout schema
  toSchema(): schema.Layout {
    const exporter = new Exporter(this);
    return exporter.exportCell(this);
  }

  // Create a new empty Layout
  static new(ident: Ident): Layout {
    return new Layout(
      ident,
      {
        bbox: graphics.Bbox.empty(),
        pins: [],
        blockages: [],
      },
      {
        elems: [],
        instances: [],
        routes: [],
        conns: [],
      }
    );
  }

  // Get the union of all bboxes defined in the impl
  bbox(): Result<Bbox> {
    if (this.impl) {
      return Result.Ok(Impl.bbox(this.impl));
    }
    return Result.Err({ why: "no-impl" });
  }
}

export interface LayoutContent {
  contentKind: "layout"; // Content Kind Tag
  layout: Layout;
  stack?: Stack;
}

export const ArrayXform = {
  // Convert to "unlinked" segments
  // Note: dx/dy are array spacing in parent coordinates (not affected by instance reflection).
  // The reflection only affects how each element is rendered, not where they're placed.
  toSegments: (array: ArrayXform): Array<LayoutXform> => {
    const { loc, reflect, nrows, ncols, dx, dy } = array;

    const segments: Array<LayoutXform> = [];
    for (let i = 0; i < nrows; i++) {
      for (let j = 0; j < ncols; j++) {
        const xform: LayoutXform = {
          loc: {
            x: loc.x + j * dx,
            y: loc.y + i * dy,
          },
          reflect,
          rotation: array.rotation,
        };
        segments.push(xform);
      }
    }
    return segments;
  },
};

export const ArrayInstance = {
  // Convert to a multi-segment instance
  toSegments: (inst: ArrayInstance): SegmentedInstance => {
    const { name, target, place } = inst;
    return {
      name,
      target,
      place: {
        kind: "segments",
        segments: ArrayXform.toSegments(place.array),
      },
    };
  },
};

export const Stack = {
  empty: (id: schema.StackId): Stack => ({
    id,
    name: `Stack ${id}`,
    base: [0, 0],
    totalLayers: 0,
    metalLayers: [],
    vias: [],
  }),
  validator: new v.Validator(v.createValidate<Stack>()),

  // Get the lowest metal layer (by layer ID, not index)
  getLowestMetalLayer: (stack: Stack): Result<MetalLayer> => {
    if (!stack.metalLayers || stack.metalLayers.length === 0) {
      return Result.Err({
        why: "no-metal-layers",
        stackId: stack.id,
      });
    }
    const lowest = stack.metalLayers.reduce(
      (min: MetalLayer, layer: MetalLayer) =>
        layer.layer < min.layer ? layer : min
    );
    return Result.Ok(lowest);
  },

  // Get the highest metal layer (by layer ID, not index)
  getHighestMetalLayer: (stack: Stack): Result<MetalLayer> => {
    if (!stack.metalLayers || stack.metalLayers.length === 0) {
      return Result.Err({
        why: "no-metal-layers",
        stackId: stack.id,
      });
    }
    const highest = stack.metalLayers.reduce(
      (max: MetalLayer, layer: MetalLayer) =>
        layer.layer > max.layer ? layer : max
    );
    return Result.Ok(highest);
  },

  // Find a metal layer by layer ID and purpose
  findMetalLayer: (
    stack: Stack,
    layerId: number,
    purposeId: number = 0
  ): Result<MetalLayer> => {
    if (!stack.metalLayers || stack.metalLayers.length === 0) {
      return Result.Err({
        why: "no-metal-layers",
        stackId: stack.id,
      });
    }
    const layer = stack.metalLayers.find(
      (metal: MetalLayer) =>
        metal.layer === layerId && metal.purpose === purposeId
    );
    if (!layer) {
      return Result.Err({
        why: "metal-layer-not-found",
        stackId: stack.id,
        layer: layerId,
        purpose: purposeId,
        availableLayers: stack.metalLayers.map((m: MetalLayer) => ({
          layer: m.layer,
          purpose: m.purpose,
        })),
      });
    }
    return Result.Ok(layer);
  },
};

export const LayoutXform = {
  identity: (): LayoutXform => ({
    loc: { x: 0, y: 0 },
    reflect: { horiz: false, vert: false },
    rotation: 0,
  }),

  eq: (a: LayoutXform, b: LayoutXform): boolean => {
    return (
      Point.eq(a.loc, b.loc) &&
      a.reflect.horiz === b.reflect.horiz &&
      a.reflect.vert === b.reflect.vert &&
      (a.rotation ?? 0) === (b.rotation ?? 0)
    );
  },

  clone: (xform: LayoutXform): LayoutXform => {
    return {
      loc: Point.clone(xform.loc),
      reflect: { horiz: xform.reflect.horiz, vert: xform.reflect.vert },
      rotation: xform.rotation,
    };
  },

  toXform: (layoutXform: LayoutXform): Xform => {
    return {
      ...layoutXform.loc,
      ...LayoutXform.convertSomeOrientation(layoutXform),
    };
  },

  convertSomeOrientation: (layoutOrient: LayoutXform): Orientation => {
    const { horiz, vert } = layoutOrient.reflect;
    // Get base rotation from reflect flags
    let baseRotation: number;
    let reflected: boolean;
    if (!vert && !horiz) {
      reflected = false;
      baseRotation = 0;
    } else if (!vert && horiz) {
      reflected = true;
      baseRotation = 180;
    } else if (vert && !horiz) {
      reflected = true;
      baseRotation = 0;
    } else {
      reflected = false;
      baseRotation = 180;
    }

    // Add explicit rotation from the LayoutXform
    const explicitRotation = layoutOrient.rotation ?? 0;
    const totalRotation = ((baseRotation + explicitRotation) %
      360) as schema.Rotation;

    return { reflected, rotation: totalRotation };
  },
};

export const Instance = {
  bbox: (instance: Instance): Bbox => {
    const { target, place } = instance;

    // Get the target bbox
    let targetBbox: Bbox;
    if (target.kind === "bound") {
      const layout = target.layout;
      targetBbox =
        graphics.Bbox.eq(layout.abs.bbox, graphics.Bbox.empty()) && layout.impl
          ? Impl.bbox(layout.impl)
          : layout.abs.bbox;
    } else {
      return graphics.Bbox.empty();
    }

    // Handle different place variants
    if (!place) {
      return graphics.Bbox.empty();
    }

    switch (place.kind) {
      case "scalar": {
        const transformer = LayoutXform.toXform(place.xform);
        return Xform.transformBbox(transformer, targetBbox);
      }
      case "segments": {
        // Handle segmented instances
        let bbox_union: Bbox = Bbox.empty();
        for (const segment of place.segments) {
          const s = segment ?? LayoutXform.identity();
          const transformer = LayoutXform.toXform(s);
          const bbox = transformer
            ? Xform.transformBbox(transformer, targetBbox)
            : graphics.Bbox.empty();
          bbox_union = Bbox.encompassingBoth(bbox, bbox_union);
        }
        return bbox_union;
      }
      case "array": {
        // Convert array to segments and process
        const segments = ArrayXform.toSegments(place.array);
        let bbox_union: Bbox = Bbox.empty();
        for (const segment of segments) {
          const transformer = LayoutXform.toXform(segment);
          const bbox = Xform.transformBbox(transformer, targetBbox);
          bbox_union = Bbox.encompassingBoth(bbox, bbox_union);
        }
        return bbox_union;
      }
      default:
        return graphics.Bbox.empty();
    }
  },
};

export const Impl = {
  bbox: (impl: Impl): Bbox => {
    let bbox: Bbox | undefined = undefined;

    for (const element of impl.elems) {
      const next = graphics.LayeredElement.bbox(element);
      if (bbox === undefined) {
        bbox = next;
        continue;
      }
      bbox = Bbox.encompassingBoth(next, bbox);
    }

    for (const instance of impl.instances) {
      const next = Instance.bbox(instance);
      if (bbox === undefined) {
        bbox = next;
        continue;
      }
      bbox = Bbox.encompassingBoth(next, bbox);
    }

    if (bbox === undefined) {
      return Bbox.empty();
    }
    return bbox;
  },
};

//
// # Schema Importer
//
// From a v4 `Layout` schema object, build a model `Layout` object
// Converts schema types (string layer names) to model types (MetalLayer objects)
//
class Importer {
  private layoutIdent?: schema.Ident;

  constructor(
    private readonly depot: DepotGet,
    private readonly namespace: string,
    private readonly stack?: Stack,
    private readonly pdkId?: string,
    private readonly pdk?: schema.pdk.PDK
  ) {}

  async importLayout(
    ctx: Context,
    layout: schema.Layout
  ): Promise<Result<Layout>> {
    this.layoutIdent = layout.ident;
    // Import implementation if present
    let impl: Impl | undefined = undefined;
    if (layout.impl) {
      const implResult = await this.importImpl(ctx, layout.impl);
      if (!implResult.ok) {
        return Result.Err(implResult.val);
      }
      impl = implResult.val;
    }

    // Import abstract content (pins and blockages)
    const absResult = this.importAbstract(layout.abs);
    if (!absResult.ok) {
      return Result.Err(absResult.val);
    }
    let abs = absResult.val;

    // Compute bounding box from implementation and pins if present.
    // This ensures layouts in the static cache always have valid bounding boxes.
    // This is part of derived metadata that should be computed on load.
    {
      let computedBbox: Bbox | undefined = impl ? Impl.bbox(impl) : undefined;

      // Include pin shapes in the bbox
      for (const pin of abs.pins) {
        const box = pinBbox(pin);
        computedBbox = computedBbox
          ? Bbox.encompassingBoth(computedBbox, box)
          : box;
      }

      if (computedBbox) {
        // bbox always tracks the geometry (recomputed on every import).
        //
        // The outline is re-snapshotted to the geometry ONLY for composite cells
        // (those with child instances), whose footprint is placement-derived and
        // legitimately changes as instances move/resize — that's what keeps a
        // shrunk child's outline fresh instead of stale. Leaf cells (geometry,
        // no instances) keep their stored outline: it may be a deliberate,
        // load-bearing outline padded wider than the geometry — e.g. a PDK FET's
        // CPP-abutment outline (crates/.../cc_cell) that arrays FETs on the poly
        // pitch. Clobbering that to the tight bbox would corrupt IVA containment
        // and outline-calc for those cells.
        //
        // Consequence: a manual `resize_outline` on a composite is transient
        // across reloads (it re-snapshots). Preserving a deliberate outline on a
        // composite would need an explicit manual/auto flag (follow-up).
        const isComposite = !!impl && impl.instances.length > 0;
        const outlineStale =
          isComposite &&
          (!abs.outline || !graphics.Bbox.eq(computedBbox, abs.outline));
        abs = {
          ...abs,
          bbox: computedBbox,
          outline: outlineStale ? computedBbox : abs.outline,
        };
      }
    }

    // Create the model layout
    const modelLayout = new Layout(
      layout.ident,
      abs,
      impl,
      this.stack,
      layout.properties,
      this.pdkId,
      this.pdk
    );

    return Result.Ok(modelLayout);
  }

  importAbstract(abs: schema.layout.Abstract): Result<Abstract> {
    const pins: Array<LayoutPin> = [];
    const blockages: Array<Blockage> = [];

    // Import pins
    if (abs.pins) {
      for (const pin of abs.pins) {
        pins.push(this.importPin(pin));
      }
    }

    // Import blockages
    // Skip blockages with unresolvable layers (e.g. layer ID not in stack) rather
    // than failing the entire content load. An invalid blockage layer should not
    // prevent the rest of the layout from loading.
    if (abs.blockages) {
      for (const blockage of abs.blockages) {
        const blockageResult = this.importBlockage(blockage);
        if (!blockageResult.ok) {
          logger.warn(
            `[importAbstract] Skipping blockage id=${
              blockage.id
            } with invalid layer ${blockage.layer}: ${JSON.stringify(
              blockageResult.val
            )}`
          );
          continue;
        }
        blockages.push(blockageResult.val);
      }
    }

    return Result.Ok({
      bbox: abs.bbox,
      outline: abs.outline,
      pins,
      blockages,
    });
  }

  async importImpl(
    ctx: Context,
    impl: schema.layout.Impl
  ): Promise<Result<Impl>> {
    const modelRoutes: Array<Route> = [];

    // Collect unique target idents from all instances (including per-segment targets)
    const targetIdents = new IdentSet();
    for (const instance of impl.instances) {
      targetIdents.add(instance.target);
      if (instance.place?.kind === "segments") {
        for (const seg of instance.place.segments) {
          if (seg?.target) {
            targetIdents.add(seg.target);
          }
        }
      }
    }

    // Fetch all unique layouts in parallel
    // Note this is a key step for network-based imports;
    // without it each layout is requested serially.
    // For message-based retrieval this is particularly slow!
    const layoutPromises = targetIdents
      .toArray()
      .map((ident) => Layout.getLayout(ctx, ident, this.depot, this.namespace));
    await Promise.all(layoutPromises);

    // Convert instances using the cached layouts
    const modelInstances: Array<Instance> = [];
    for (const instance of impl.instances) {
      const { target, ...rest } = instance;
      const layout = Layout.layouts.get(target);
      if (!layout) {
        // An unbound instance renders NOTHING, so a master that failed to load
        // is indistinguishable from a device that was never placed. Said out
        // loud, because it was not: when the OA pin import started producing
        // masters the model refused (`{why: "no-stack"}`), every placed foundry
        // device went blank with no console output at all, and finding it took a
        // browser session rather than a log line. `importInstance` — the
        // non-batch path below — has always logged this; this path never did.
        logger.warn(
          `[Layout.importImpl] instance '${instance.name}' has no master: ` +
            `${JSON.stringify(instance.target)} failed to load, so it will ` +
            `render as nothing`
        );
        // Create an unbound instance
        modelInstances.push({
          ...rest,
          target: {
            kind: "unbound",
            ident: instance.target,
          },
        });
      } else {
        modelInstances.push({
          ...rest,
          target: {
            kind: "bound",
            layout,
          },
        });
      }
    }

    if (impl.routes) {
      for (const route of impl.routes) {
        const routeResult = this.importRoute(route);
        if (!routeResult.ok) {
          return Result.Err(routeResult.val);
        }
        modelRoutes.push(routeResult.val);
      }
    }

    return Result.Ok({
      elems: impl.elems,
      instances: modelInstances,
      routes: modelRoutes,
      conns: impl.conns,
    });
  }

  importRoute(route: schema.layout.Route): Result<Route> {
    const modelPath: Array<ModelRoutePoint> = [];
    for (const routePoint of route.path) {
      const layerResult = this.importMetalLayer(routePoint.layerId);
      if (!layerResult.ok) {
        return Result.Err(layerResult.val);
      }
      modelPath.push({
        point: routePoint.point,
        layer: layerResult.val,
        width: routePoint.width,
      });
    }
    // Spread the whole route so every scalar field (id, locked, and any future
    // ones) round-trips; only `path` is transformed to resolved layers (with
    // each point's per-segment width carried across).
    return Result.Ok({ ...route, path: modelPath });
  }

  /**
   * A pin imports as stored. It has no layer of its own to resolve; each shape
   * names its own, and a shape off the stack is kept and written back as is.
   */
  importPin(pin: schema.layout.LayoutPin): LayoutPin {
    return { ...pin };
  }

  importBlockage(blockage: schema.layout.Blockage): Result<Blockage> {
    const layer = this.importMetalLayer(blockage.layer);
    return layer.andThen((layer) => Result.Ok({ ...blockage, layer }));
  }

  // Import a MetalLayer from the stack by layer ID and purpose ID
  importMetalLayer(layerId: number, purposeId: number = 0): Result<MetalLayer> {
    if (!this.stack) {
      return Result.Err({ why: "no-stack" });
    }
    const result = Stack.findMetalLayer(this.stack, layerId, purposeId);
    if (result.err) {
      // Map the error to "invalid-layer-purpose" for backward compatibility
      // Existing code expects this specific error format with availableLayers
      // This maintains the same error structure as before refactoring
      return Result.Err({
        why: "invalid-layer-purpose",
        layer: layerId,
        purpose: purposeId,
        availableLayers: this.stack.metalLayers.map((m) => ({
          layer: m.layer,
          purpose: m.purpose,
        })),
      });
    }
    return result;
  }

  async importInstance(
    ctx: Context,
    instance: schema.layout.Instance
  ): Promise<Result<Instance>> {
    const { target, ...rest } = instance;
    const layoutResult = await Layout.getLayout(
      ctx,
      target,
      this.depot,
      this.namespace
    );
    if (layoutResult.err) {
      // Create an unbound instance.
      logger.error(layoutResult);
      return Result.Ok({
        ...rest,
        target: {
          kind: "unbound",
          ident: instance.target,
        },
      });
    }
    return Result.Ok({
      ...rest,
      target: {
        kind: "bound",
        layout: layoutResult.val,
      },
    });
  }
}

// Schema Exporter - converts model types to v4 schema types
class Exporter {
  constructor(private top: Layout) {}

  exportCell(layout: Layout): LayoutCell {
    // For v4, export a single layout without hierarchy traversal
    // Referenced layouts remain as Ident references in instances
    const cellSchema = LayoutCell.new(layout.ident);
    cellSchema.abs = this.exportAbstract(layout.abs);
    cellSchema.impl = layout.impl ? this.exportImpl(layout.impl) : undefined;
    cellSchema.pdkId = layout.pdkId;
    cellSchema.stackId = layout.stack?.id;
    cellSchema.properties = layout.properties;

    return cellSchema;
  }

  // Convert implementation to schema representation
  private exportImpl(impl: Impl): schema.layout.Impl {
    return {
      ...impl,
      instances: impl.instances.map((instance) =>
        this.exportInstance(instance)
      ),
      routes: impl.routes.map((route) => this.exportRoute(route)),
    };
  }

  // Convert instance to schema representation
  private exportInstance(instance: Instance): schema.layout.Instance {
    const { target: originalTarget, ...rest } = instance;
    const target =
      originalTarget.kind === "bound"
        ? originalTarget.layout.ident
        : originalTarget.ident;
    return {
      ...rest,
      target,
    };
  }

  private exportAbstract(abs: Abstract): schema.layout.Abstract {
    return {
      ...abs,
      pins: abs.pins.map((pin) => this.exportPin(pin)),
      blockages: abs.blockages.map((blockage) => this.exportBlockage(blockage)),
    };
  }

  private exportPin(pin: LayoutPin): schema.layout.LayoutPin {
    return { ...pin };
  }

  private exportBlockage(blockage: Blockage): schema.layout.Blockage {
    return {
      ...blockage,
      layer: blockage.layer.layer,
    };
  }

  private exportRoute(route: Route): schema.layout.Route {
    // Spread the whole route so every scalar field (id, locked, width, and any
    // future ones) round-trips; only `path` is transformed back to layer ids.
    return {
      ...route,
      path: route.path.map((rp) => ({
        point: rp.point,
        layerId: rp.layer.layer,
        width: rp.width,
      })),
    };
  }
}
